import { addLogoAndHeader, addFooterToWorksheet } from './header-footer.js';
import { createWBSHeaderRow, createFlatHeaderRow, createGroupedHeaderRow } from './column-headers.js';
import { createWBSCell, createDataCell, createFlatDataCell, createWBSNameCell } from './cell-creators.js';
import { createGroupRowWithIndents, createGroupRowWithoutIndents } from './group-creators.js';
import { findColumnIndexByKey, findFirstAggregationColumnIndex } from '../utils/columns.js';
import { getAggregationValueById, getTotalAggregationValue } from '../data/aggregation.js';
import { applyCellStyle } from '../utils/excel-helpers.js';
import { EXCEL_STYLES, EXCEL_CONSTANTS } from '../constants.js';

export const createWorksheetWBSFormat = (exporter, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, maxNestingLevel, aggregationData) => {
  const totalColumns = flatColumns.length + maxNestingLevel;
  const changedNameColumn = exporter.options.constants.nameTaskColumnKey ? exporter.options.constants.nameTaskColumnKey:  EXCEL_CONSTANTS.nameTaskColumnKey;
  const nameColumnIndex = findColumnIndexByKey(flatColumns, changedNameColumn);
  
  let currentRow = addLogoAndHeader(worksheet, workbook, totalColumns, exporter.options);
  createWBSHeaderRow(worksheet, currentRow, flatColumns, columnHeaders, maxNestingLevel, nameColumnIndex, exporter.options.colorMap);
  currentRow++;
  
  if (exporter.shouldAddTotalAggregation(aggregationData)) {
    exporter.addTotalAggregationRow(worksheet, currentRow, flatColumns, maxNestingLevel, aggregationData);
    currentRow++;
  }
  
  processedTasks.forEach(task => {
    const row = worksheet.getRow(currentRow);
    const isParent = task.isParent;
    const level = task.level || 0;
    const taskId = exporter.getTaskId(task);
    const taskType = exporter.getTaskData(task, 'type');
    
    let taskNameStartCol = 1;
    for (let i = 0; i < nameColumnIndex; i++) taskNameStartCol++;
    
    let currentDataCol = 1;
    
    flatColumns.forEach((column, colIndex) => {
      if (column.key === changedNameColumn) {
        currentDataCol = createWBSNameCell(worksheet, row, task, taskNameStartCol, level, maxNestingLevel, isParent);
      } else if (column.key === 'wbs') {
        const result = createWBSCell(row, task, column, currentDataCol, exporter.fieldHandlers, isParent);
        currentDataCol = result.nextCol;
      } else {
        const result = createDataCell(
          row, task, column, currentDataCol, taskId, taskType, isParent, 
          aggregationData, exporter.fieldHandlers, exporter.options, worksheet
        );
        currentDataCol = result.nextCol;
      }
    });
    
    currentRow++;
  });
  
  if (exporter.options.includeFooter) {
    addFooterToWorksheet(worksheet, currentRow, totalColumns, exporter.options);
  }
  
  return worksheet;
};

export const createWorksheetFlatFormat = (exporter, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, aggregationData) => {
  const totalColumns = flatColumns.length;
  
  let currentRow = addLogoAndHeader(worksheet, workbook, totalColumns, exporter.options);
  createFlatHeaderRow(worksheet, currentRow, columnHeaders, flatColumns);
  currentRow++;
  
  if (exporter.shouldAddTotalAggregation(aggregationData)) {
    exporter.addTotalAggregationRow(worksheet, currentRow, flatColumns, 0, aggregationData);
    currentRow++;
  }
  
  processedTasks.forEach(task => {
    const row = worksheet.getRow(currentRow);
    const taskId = exporter.getTaskId(task);
    const taskType = exporter.getTaskData(task, 'type');
    
    flatColumns.forEach((column, colIndex) => {
      createFlatDataCell(row, task, column, colIndex, taskId, taskType, aggregationData, exporter.fieldHandlers, exporter.options, worksheet);
    });
    
    currentRow++;
  });
  
  if (exporter.options.includeFooter) {
    addFooterToWorksheet(worksheet, currentRow, totalColumns, exporter.options);
  }
  
  return worksheet;
};

export const createWorksheetGroupedFormat = (exporter, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, maxNestingLevel, aggregationData) => {
  const totalColumns = flatColumns.length + (maxNestingLevel > 1 ? maxNestingLevel : 0);
  const changedNameColumn = exporter.options.constants?.nameTaskColumnKey || EXCEL_CONSTANTS.nameTaskColumnKey;
  const nameColumnIndex = findColumnIndexByKey(flatColumns, changedNameColumn);
  const hasIndents = maxNestingLevel > 1;
  
  let currentRow = addLogoAndHeader(worksheet, workbook, totalColumns, exporter.options);
  createGroupedHeaderRow(worksheet, currentRow, flatColumns, columnHeaders, maxNestingLevel, nameColumnIndex, exporter.options.colorMap);
  currentRow++;
  
  if (exporter.shouldAddTotalAggregation(aggregationData)) {
    exporter.addTotalAggregationRow(worksheet, currentRow, flatColumns, hasIndents ? maxNestingLevel : 0, aggregationData);
    currentRow++;
  }
  
  const groupStack = [];
  let lastRootGroupIndex = -1;
  
  for (let i = 0; i < processedTasks.length; i++) {
    const task = processedTasks[i];
    const row = worksheet.getRow(currentRow);
    const taskLevel = task.level || 0;
    
    while (groupStack.length > 0 && taskLevel <= groupStack[groupStack.length - 1].level) {
      groupStack.pop();
    }
    
    if (task.isGroupHeader) {
      groupStack.push({
        level: taskLevel,
        index: i,
        row: currentRow,
        title: task.title
      });
      
      if (taskLevel === 0) {
        lastRootGroupIndex = i;
      }
      
      if (hasIndents) {
        createGroupRowWithIndents(worksheet, row, task, flatColumns, maxNestingLevel, aggregationData, exporter.fieldHandlers, exporter.options);
      } else {
        createGroupRowWithoutIndents(worksheet, row, task, flatColumns, aggregationData, exporter.fieldHandlers, exporter.options);
      }
      
      currentRow++;
      
    } else {
      // Обычная задача
      const indent = task.level || 0;
      const taskId = exporter.getTaskId(task);
      const taskType = exporter.getTaskData(task, 'type');
      
      if (hasIndents) {
        for (let col = 1; col <= indent; col++) {
          const cell = row.getCell(col);
          cell.value = '';
        }
      }
      
      flatColumns.forEach((column, colIndex) => {
        const actualColIndex = hasIndents ? maxNestingLevel + colIndex + 1 : colIndex + 1;
        const cell = row.getCell(actualColIndex);
        const columnKey = column.key;
        
        const aggregationValue = exporter.options.includeAggregation && taskId ? 
          getAggregationValueById(taskId, columnKey, aggregationData) : null;
        
        let result = null; 

        if (column.key === 'level') {
          if (exporter.options.enableLevel) {
            const handler = exporter.fieldHandlers[column.type] || exporter.fieldHandlers.default;
            result = handler(column, false);
            if (result.style) applyCellStyle(cell, result.style);
            cell.value = task.level + 1 || 0;
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }
          return;
        }
        
        if (aggregationValue !== null && column.type === 'aggregation') {
          const handler = exporter.fieldHandlers[column.type] || exporter.fieldHandlers.default;
          result = handler(aggregationValue, column, false);
          cell.value = result.value || '';
          if (result.style) applyCellStyle(cell, result.style);
        } else {
          if (column.key === 'type' || column.type === 'type') {
            cell.value = '';
            const typeStyle = exporter.fieldHandlers.type(taskType, false);
            if (typeStyle.style) applyCellStyle(cell, typeStyle.style);
          } else {
            const handler = exporter.fieldHandlers[column.type] || exporter.fieldHandlers.default;
            const value = exporter.getTaskData(task, column.key);
            result = handler(value, column);
            cell.value = result.value || '';
            if (result.style) applyCellStyle(cell, result.style);
          }
        }
        
        if (result && result.colWidth && !worksheet.getColumn(actualColIndex).width) {
          worksheet.getColumn(actualColIndex).width = result.colWidth;
        }
      });
      
      currentRow++;
      
      // Проверяем, является ли текущая задача последней в своей корневой группе
      const nextTask = processedTasks[i + 1];
      
      // Если это последняя задача в текущей корневой группе
      // (следующая задача - корневая группа или нет следующей задачи)
      const isLastInRootGroup = nextTask ? nextTask.level === 0 : false;
      
      // Добавляем пустую строку только если есть следующая корневая группа
      if (isLastInRootGroup) {
        // Добавляем пустую строку после завершения всех задач группы
        const emptyRow = worksheet.getRow(currentRow);
        
        // Заполняем пустые ячейки для отступов
        if (hasIndents) {
          for (let col = 1; col <= maxNestingLevel; col++) {
            const cell = emptyRow.getCell(col);
            cell.value = '';
          }
        }
        
        // Остальные ячейки оставляем пустыми
        for (let col = hasIndents ? maxNestingLevel + 1 : 1; col <= totalColumns; col++) {
          const cell = emptyRow.getCell(col);
          cell.value = '';
        }
        
        currentRow++;
      }
    }
  }
  
  if (exporter.options.includeFooter) {
    addFooterToWorksheet(worksheet, currentRow, totalColumns, exporter.options);
  }
  
  return worksheet;
};